/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from '@app/user/services/users.service';
import { getCurrentUser } from '@app/user/store/users.selectors';

import { UserDetailComponent } from '@app/user/user-detail/user-detail.component';
import { UserProfileComponent } from '@app/user/user-profile/user-profile.component';
import * as AuthActions from '@auth/store/auth.actions';
import { currentUser } from '@auth/store/auth.selectors';
import { State } from '@core/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@shared/models';
import { extractConstraintViolations, extractHttpErrorMessage } from '@shared/utils';
import { OAuthService } from 'angular-oauth2-oidc';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as UsersActions from './users.actions';
import {
  UserDeactivationConfirmationComponent
} from '@users/user-deactivation-confirmation/user-deactivation-confirmation.component';

@Injectable()
export class UsersEffects {
  constructor(
    private actions$: Actions<UsersActions.UsersActions>,
    private usersService: UsersService,
    private oauthService: OAuthService,
    private translateService: TranslateService,
    private snackBarService: MatSnackBar,
    private dialogService: MatDialog,
    private store: Store<State>
  ) {
  }

  
  loadUsers$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.LOAD_USERS),
    switchMap(() => this.usersService.getUsers().pipe(
      map(users => new UsersActions.LoadUsersSuccess(users)),
      catchError(() => of(new UsersActions.LoadUsersFailure()))
    ))
  ));

  
  loadUsersFailure$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.LOAD_USERS_FAILURE),
    switchMap(() => this.translateService.get('user.errors.loadMany').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), {dispatch: false});

  
  editUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.EDIT_USER),
    map(action => action.userId),
    tap(userId => this.dialogService.open(UserDetailComponent, {
      width: '25rem',
      data: {userId}
    }))
  ), {dispatch: false});

  
  deactivateUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.DEACTIVATE_USER),
    map((action: UsersActions.DeactivateUser) => action.user),
    switchMap(user => this.dialogService.open(UserDeactivationConfirmationComponent, {
        data: {user}
      }).afterClosed().pipe(
        map(confirmation => [confirmation, user])
      )
    ),
    filter(confirmation => confirmation[0] === true),
    map(confirmation => confirmation[1] as User),
    switchMap(user => this.usersService.deactivateUser(user)),
    tap(_ => this.store.dispatch(new UsersActions.LoadUsers()))
  ), {dispatch: false});

  
  editUserProfile$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.EDIT_USER_PROFILE),
    map(action => action.userId),
    tap(userId => this.dialogService.open(UserProfileComponent, {
      width: '25rem',
      data: {userId}
    }))
  ), {dispatch: false});

  
  loadUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.LOAD_USER),
    map(action => action.userId),
    switchMap(userId => {
      const getUser$ = of(userId);

      const createUser$ = getUser$.pipe(
        filter(id => !id),
        switchMap(() => of({} as User))
      );

      const loadUser$ = getUser$.pipe(
        filter(id => !!id),
        switchMap(id => this.usersService.getUser(id))
      );

      return merge(createUser$, loadUser$).pipe(
        map(user => new UsersActions.LoadUserSuccess(user)),
        catchError(() => of(new UsersActions.LoadUserFailure()))
      );
    })
  ));

  
  loadUserFailure$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.LOAD_USER_FAILURE),
    switchMap(() => this.translateService.get('user.errors.loadOne').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), {dispatch: false});

  
  persistUser$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.PERSIST_USER),
    map(action => action.user),
    withLatestFrom(this.store.select(getCurrentUser)),
    switchMap(([user, selectedUser]) => {
      const existingId = selectedUser['@id'];
      // Die Action-Payload ist eingefroren, daher kopieren statt mutieren.
      const userToPersist: User = existingId ? {...user, '@id': existingId} : user;

      const persistUser$: Observable<User> = existingId
        ? this.usersService.updateUser(userToPersist).pipe(
            withLatestFrom(this.store.select(currentUser)),
            tap(([updatedUser, authenticatedUser]) => {
              if (authenticatedUser['@id'] === updatedUser['@id'] ) {
                this.oauthService.loadUserProfile();
                this.store.dispatch(new AuthActions.Init({authenticated: true, user: updatedUser}));
              }
            }),
            map(([updatedUser]) => updatedUser)
          )
        : this.usersService.createUser(userToPersist);

      return persistUser$.pipe(
        map(() => new UsersActions.PersistUserSuccess()),
        catchError(error => of(new UsersActions.PersistUserFailure(extractConstraintViolations(error))))
      );
    })
  ));

  
  persistUserSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.PERSIST_USER_SUCCESS),
    tap(() => this.dialogService.closeAll()),
    map(() => new UsersActions.LoadUsers())
  ));

  
  persistUserFailure$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.PERSIST_USER_FAILURE),
    switchMap((action: UsersActions.PersistUserFailure) => this.translateService.get('user.errors.persist').pipe(
      map(fallbackMessage => action.violations.length > 0
        ? action.violations.map(violation => violation.message).join(' ')
        : fallbackMessage),
      tap(errorMessage => this.snackBarService.open(errorMessage, undefined, {duration: 8000}))
    ))
  ), {dispatch: false});

  
  updateAccount$ = createEffect(() => this.actions$.pipe(
    ofType(UsersActions.UPDATE_ACCOUNT),
    map(action => {
      return action.payload;
    }),
    withLatestFrom(this.store.select(getCurrentUser)),
    switchMap(([user, selectedUser]) => {
      // Die Action-Payload ist eingefroren, daher kopieren statt mutieren.
      const userToPersist: User = {...user, '@id': selectedUser['@id']};

      return this.usersService.updateUser(userToPersist).pipe(
        switchMap( (updatedUser) => [
          new UsersActions.UpdateAccountSuccess(),
          new AuthActions.Init({authenticated: true, user: updatedUser}),
          new UsersActions.LoadUsers()
        ]),
        tap(() => {
          return this.dialogService.closeAll();
        }),
        catchError((error: any) => {
          const errorMessage = extractHttpErrorMessage(error);
          return of(new UsersActions.UpdateAccountFailure(errorMessage));
        })
      );
    })
  ));
}
